// Phase 1a: TanStack Query bindings for the Capture command room. The
// orchestration RPCs live on zou's ProjectDataService (CreateCaptureProject,
// ListCaptureProjects, GetCaptureWorkspace, and the RFI / interaction /
// workstream upserts) — one round-trip per user action. Each mutation
// threads one workspace correlation so the audit timeline groups a session.
import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError, createClient } from '@connectrpc/connect';
import { CaptureTaskProvenanceService, PursuitService } from '@mono/proto-clients';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { projectDataClient } from '../../lib/connect/clients';
import { connectTransport } from '../../lib/connect/transport';
import { invalidatePursuitReads } from './record/pursuitQueryKeys';
// JS Date -> proto Timestamp, undefined when unset (the wire default).
function toTs(d) {
    return d ? Timestamp.fromDate(d) : undefined;
}
const CAPTURE_LIST_KEY = 'capture-list';
export const CAPTURE_KEY = 'capture';
/** List the tenant's capture projects for the list page. */ export function useCaptureProjects() {
    return useQuery({
        queryKey: [
            CAPTURE_LIST_KEY
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listCaptureProjects({});
            return resp.captures;
        }
    });
}
/** GRW-14: React Query root of `ListPursuits` (key `[root, tenant, lifecycle, pageToken, search]`). */ export const PURSUITS_KEY = 'pursuits';
/**
 * GRW-11 `ListPursuits`: one keyset page of the Pursuits list plus the
 * Live / Parked / Closed counts over pursuits the caller may read.
 */ export function useListPursuits(args) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            PURSUITS_KEY,
            user?.tenant_id,
            args.lifecycle,
            args.pageToken ?? '',
            args.search ?? ''
        ],
        queryFn: ()=>projectDataClient.listPursuits({
                lifecycle: args.lifecycle,
                pageToken: args.pageToken ?? '',
                search: args.search ?? '',
                pageSize: args.pageSize ?? 50
            }),
        enabled: (args.enabled ?? true) && !!user?.id,
        placeholderData: keepPreviousData,
        retry: false
    });
}
const pursuitClient = createClient(PursuitService, connectTransport);
/** The Opportunity/Qualification a Pursuit was opened from (persisted link, PUR-01). */ export function usePursuitOpening(captureProjectId) {
    return useQuery({
        queryKey: [
            CAPTURE_KEY,
            captureProjectId,
            'pursuit-opening'
        ],
        enabled: !!captureProjectId,
        queryFn: ()=>pursuitClient.getPursuitOpening({
                captureProjectId
            })
    });
}
/** Create a capture project (spawns the zou project + pangaea row). */ export function useCreateCaptureProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.createCaptureProject({
                name: args.name,
                opportunityHypothesis: args.opportunityHypothesis ?? '',
                accountId: args.accountId ?? '',
                customerOrgId: args.customerOrgId ?? ''
            });
            if (!resp.summary) throw new Error('CreateCaptureProject returned no summary');
            return resp.summary;
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_LIST_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PURSUITS_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create capture project');
        }
    });
}
/** Update a capture project's editable metadata (name, stage, hypothesis). */ export function useUpdateCaptureProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.updateCaptureProject({
                captureProjectId: args.captureProjectId,
                name: args.name ?? '',
                stage: args.stage ?? '',
                businessPhase: args.businessPhase ?? '',
                opportunityHypothesis: args.opportunityHypothesis ?? '',
                accountId: args.accountId ?? '',
                customerOrgId: args.customerOrgId ?? ''
            });
            if (!resp.summary) throw new Error('UpdateCaptureProject returned no summary');
            return resp.summary;
        },
        onSuccess: (_summary, vars)=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    'pursuit-graph'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_LIST_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update capture project');
        }
    });
}
/**
 * Open the source-linked Proposal. A retry with the same `openRequestKey` returns the same
 * Proposal; `deliberateNewBid` starts another bid from the Pursuit without closing it.
 */ export function useConvertCaptureToProposal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ captureProjectId, openRequestKey, deliberateNewBid = false })=>{
            const resp = await projectDataClient.convertCaptureToProposal({
                captureProjectId,
                openRequestKey,
                deliberateNewBid
            });
            if (!resp.summary) throw new Error('ConvertCaptureToProposal returned no summary');
            return resp.summary;
        },
        onSuccess: (_summary, { captureProjectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_LIST_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Load the Capture command room: the project summary plus its RFI events,
 * interactions, and workstream items in one round-trip.
 */ export function useCaptureWorkspace(captureProjectId) {
    return useQuery({
        queryKey: [
            CAPTURE_KEY,
            captureProjectId
        ],
        enabled: !!captureProjectId,
        queryFn: async ()=>{
            const resp = await projectDataClient.getCaptureWorkspace({
                captureProjectId
            });
            if (!resp.summary) throw new Error('GetCaptureWorkspace returned no summary');
            return resp;
        }
    });
}
export function useUpsertRfiEvent(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertRfiEvent({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                title: args.title,
                sourceNoticeId: args.sourceNoticeId ?? '',
                rfiType: args.rfiType ?? 'rfi',
                eventDate: toTs(args.eventDate),
                responseDue: toTs(args.responseDue),
                questions: args.questions ?? '',
                responseArtifactId: args.responseArtifactId ?? '',
                impactedRequirementsJson: args.impactedRequirementsJson ?? '',
                impactedVehicleStrategy: args.impactedVehicleStrategy ?? '',
                notes: args.notes ?? '',
                responseStatus: args.responseStatus ?? '',
                responseReason: args.responseReason ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
            // The RFIs tab lists events from this query.
            queryClient.invalidateQueries({
                queryKey: [
                    'capture-rfi-responses',
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save RFI');
        }
    });
}
export function useUpsertCustomerInteraction(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCustomerInteraction({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                interactionDate: toTs(args.interactionDate),
                channel: args.channel ?? 'meeting',
                stakeholder: args.stakeholder ?? '',
                summary: args.summary,
                title: args.title,
                isDraft: args.isDraft,
                participantUpserts: args.participantUpserts,
                participantRemovals: args.participantRemovals,
                sentiment: args.sentiment ?? 'unknown',
                followUps: args.followUps ?? '',
                notes: args.notes ?? '',
                eventId: args.eventId
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
            // GRW-12/13: a touchpoint adds a thread event and moves people counts.
            void invalidatePursuitReads(queryClient);
        },
        onError: ()=>{
            toast.error('Save could not be confirmed. Your input is kept in the editor. Review its recovery message.');
        }
    });
}
export function useTouchpointProposals(captureProjectId, interactionId) {
    const queryClient = useQueryClient();
    const scope = {
        captureProjectId,
        interactionId
    };
    const queryKey = [
        CAPTURE_KEY,
        captureProjectId,
        'touchpoint-proposals',
        interactionId
    ];
    const history = useQuery({
        queryKey,
        enabled: !!interactionId,
        queryFn: async ()=>(await projectDataClient.listTouchpointProposals(scope)).proposals
    });
    const draft = useMutation({
        mutationFn: async (attachmentIds)=>(await projectDataClient.draftTouchpointProposals({
                ...scope,
                attachmentIds
            })).proposals,
        onSuccess: ()=>queryClient.invalidateQueries({
                queryKey
            })
    });
    const review = useMutation({
        mutationFn: async (args)=>{
            const response = await projectDataClient.reviewTouchpointProposal({
                ...scope,
                ...args
            });
            if (!response.proposal) throw new Error('Review decision could not be confirmed');
            return response.proposal;
        },
        onSuccess: (proposal)=>{
            queryClient.setQueryData(queryKey, (old)=>old?.map((row)=>row.id === proposal.id ? proposal : row));
            void queryClient.invalidateQueries({
                queryKey
            });
            if (proposal.state === 'accepted') {
                void queryClient.invalidateQueries({
                    queryKey: [
                        CAPTURE_KEY,
                        captureProjectId
                    ],
                    exact: true
                });
            }
        }
    });
    const refresh = ()=>projectDataClient.getCaptureWorkspace({
            captureProjectId
        });
    return {
        history,
        draft,
        review,
        refresh
    };
}
export function useUpsertWorkstreamItem(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertWorkstreamItem({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                workstreamType: args.workstreamType,
                title: args.title,
                body: args.body ?? '',
                status: args.status ?? 'open',
                dataJson: args.dataJson ?? '',
                aiDrafted: args.aiDrafted ?? false
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save workstream item');
        }
    });
}
/** Create or update the Pursuit's scheduled go/no-go decision record. */ export function useUpsertCaptureGoNoGoDecision(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCaptureGoNoGoDecision({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                factors: args.factors,
                dueAt: toTs(args.dueAt),
                outcome: args.outcome ?? 'pending',
                notes: args.notes ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save go/no-go decision');
        }
    });
}
// correlationHeader threads one workspace correlation across a session's
// mutations (zou reads X-Workspace-Correlation to group the audit timeline).
function correlationHeader(id) {
    return id ? {
        headers: {
            'X-Workspace-Correlation': id
        }
    } : undefined;
}
export function useUpsertCaptureProfile(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCaptureProfile({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                solicitationNumber: args.solicitationNumber ?? '',
                crmNumber: args.crmNumber ?? '',
                businessDeveloperId: args.businessDeveloperId ?? '',
                captureManagerId: args.captureManagerId ?? '',
                customerAgencyNames: args.customerAgencyNames ?? '',
                bidPosition: args.bidPosition ?? '',
                vehicle: args.vehicle ?? '',
                procurementType: args.procurementType ?? '',
                contractType: args.contractType ?? '',
                periodOfPerformance: args.periodOfPerformance ?? '',
                totalProgramValue: args.totalProgramValue ?? '',
                stageLabel: args.stageLabel ?? '',
                pwinPercent: args.pwinPercent ?? '',
                risks: args.risks ?? '',
                vehicleId: args.vehicleId
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
            // GRW-12: vehicle and set-aside are header facts.
            void invalidatePursuitReads(queryClient);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save capture profile');
        }
    });
}
export function useUpsertCaptureTeamingLink(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCaptureTeamingLink({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                companyProfileId: args.companyProfileId,
                workshare: args.workshare ?? '',
                nda: args.nda ?? false,
                ta: args.ta ?? false,
                rationale: args.rationale ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    'pursuit-graph'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save teaming link');
        }
    });
}
export function useDeleteCaptureTeamingLink(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteCaptureTeamingLink({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    'pursuit-graph'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove teammate');
        }
    });
}
export function useUpsertCaptureCompetitionLink(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCaptureCompetitionLink({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                companyProfileId: args.companyProfileId,
                role: args.role
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save competition link');
        }
    });
}
export function useDeleteCaptureCompetitionLink(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteCaptureCompetitionLink({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove competitor');
        }
    });
}
export function useUpsertCaptureTask(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCaptureTask({
                captureProjectId: args.captureProjectId,
                id: args.id ?? '',
                title: args.title,
                ownerUserId: args.ownerUserId ?? '',
                dueAt: toTs(args.dueAt),
                status: args.status ?? 'open',
                notes: args.notes ?? '',
                sortOrder: args.sortOrder ?? 0
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            // An owner the server refuses is shown inline on the row, not as a toast.
            if (err instanceof ConnectError && err.code === Code.InvalidArgument) return;
            toast.error(err instanceof Error ? err.message : 'Failed to save task');
        }
    });
}
export function useDeleteCaptureTask(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteCaptureTask({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove task');
        }
    });
}
// Stage 3 (SAD-495): competitive field + Black Hat inputs. GetCompetitiveField
// is its own aggregate read (participant + current assessment + evidence +
// strategy, plus Black Hat inputs) — not part of GetCaptureWorkspace — so it
// gets its own query key rather than folding into CAPTURE_KEY.
const COMPETITIVE_FIELD_KEY = 'competitive-field';
export function useCompetitiveField(captureProjectId) {
    return useQuery({
        queryKey: [
            COMPETITIVE_FIELD_KEY,
            captureProjectId
        ],
        enabled: !!captureProjectId,
        queryFn: async ()=>{
            return projectDataClient.getCompetitiveField({
                captureProjectId
            });
        }
    });
}
export function useUpsertCompetitiveFieldParticipant(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCompetitiveFieldParticipant({
                id: args.id ?? '',
                captureProjectId: args.captureProjectId,
                companyProfileId: args.companyProfileId,
                inclusionBasis: args.inclusionBasis,
                basisNote: args.basisNote ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    'pursuit-graph'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save competitive field participant');
        }
    });
}
export function useDeleteCompetitiveFieldParticipant(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteCompetitiveFieldParticipant({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    'pursuit-graph'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove competitive field participant');
        }
    });
}
/** Always creates the first assessment or revises the live one by new row — never an in-place edit. */ export function useRecordCompetitiveFieldAssessment(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.recordCompetitiveFieldAssessment({
                captureProjectId: args.captureProjectId,
                participantId: args.participantId,
                likelihoodBand: args.likelihoodBand,
                assertionStatus: args.assertionStatus,
                confidence: args.confidence,
                rationale: args.rationale ?? '',
                asOfDate: toTs(args.asOfDate)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record competitive field assessment');
        }
    });
}
export function useUpsertCompetitiveFieldStrategy(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCompetitiveFieldStrategy({
                id: args.id ?? '',
                captureProjectId: args.captureProjectId,
                participantId: args.participantId,
                positioning: args.positioning ?? '',
                discriminators: args.discriminators ?? '',
                response: args.response ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save competitive field strategy');
        }
    });
}
/** Appends one evidence row — there is no update or delete for evidence. */ export function useRecordCompetitorEvidence(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.recordCompetitorEvidence({
                captureProjectId: args.captureProjectId,
                participantId: args.participantId,
                sourceKind: args.sourceKind,
                sourceRef: args.sourceRef ?? '',
                sourceDate: toTs(args.sourceDate),
                claimType: args.claimType,
                stance: args.stance,
                confidence: args.confidence,
                rationale: args.rationale ?? '',
                inferenceAssumptions: args.inferenceAssumptions ?? '',
                supersedesEvidenceId: args.supersedesEvidenceId ?? '',
                basis: args.basis ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record competitor evidence');
        }
    });
}
export function useUpsertBlackHatInput(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertBlackHatInput({
                id: args.id ?? '',
                captureProjectId: args.captureProjectId,
                participantId: args.participantId,
                competitorAdvantages: args.competitorAdvantages ?? '',
                competitorVulnerabilities: args.competitorVulnerabilities ?? '',
                likelyApproaches: args.likelyApproaches ?? '',
                ourResponse: args.ourResponse ?? '',
                openQuestions: args.openQuestions ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save Black Hat input');
        }
    });
}
/** Stamps the caller's human review on a current Black Hat input. */ export function useReviewBlackHatInput(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.reviewBlackHatInput({
                captureProjectId: args.captureProjectId,
                id: args.id,
                reviewNote: args.reviewNote ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to review Black Hat input');
        }
    });
}
export function useDeleteBlackHatInput(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteBlackHatInput({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    COMPETITIVE_FIELD_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove Black Hat input');
        }
    });
}
// ============================================================================
// SAD-485 (CAP-01): Capture Plan projection — human-owned Capture Area health
// ============================================================================
//
// GetCapturePlan is a read-only projection: Capture Plan areas (plus their
// append-only health ledger) are the only state owned here — tasks, upcoming
// reviews, upcoming decisions and references are read live from their owning
// records on every call, never duplicated. Mutations below invalidate the
// capture-plan query only; health never updates optimistically because it is
// a human decision the server must resolve (decider, ledger row) before the
// UI may show it as current.
const CAPTURE_PLAN_KEY = 'capture-plan';
export function useCapturePlan(captureProjectId) {
    return useQuery({
        queryKey: [
            CAPTURE_PLAN_KEY,
            captureProjectId
        ],
        enabled: !!captureProjectId,
        queryFn: async ()=>{
            return projectDataClient.getCapturePlan({
                captureProjectId
            });
        }
    });
}
export function useUpsertCapturePlanArea(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertCapturePlanArea({
                id: args.id ?? '',
                captureProjectId: args.captureProjectId,
                name: args.name,
                leadUserId: args.leadUserId ?? '',
                targetDate: toTs(args.targetDate),
                summary: args.summary ?? '',
                sortOrder: args.sortOrder ?? 0
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save capture plan area');
        }
    });
}
export function useDeleteCapturePlanArea(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.deleteCapturePlanArea({
                captureProjectId: args.captureProjectId,
                id: args.id
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove capture plan area');
        }
    });
}
/** The one path that moves an area's health — always a human decision. */ export function useSetCapturePlanAreaHealth(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.setCapturePlanAreaHealth({
                captureProjectId: args.captureProjectId,
                areaId: args.areaId,
                health: args.health,
                reasons: args.reasons ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to set capture plan area health');
        }
    });
}
/** Records an AI proposal onto the health ledger only — never moves health itself. */ export function useSuggestCapturePlanAreaHealth(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.suggestCapturePlanAreaHealth({
                captureProjectId: args.captureProjectId,
                areaId: args.areaId,
                health: args.health,
                reasons: args.reasons ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record health suggestion');
        }
    });
}
export function useSetCaptureTaskPlanArea(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.setCaptureTaskPlanArea({
                captureProjectId: args.captureProjectId,
                taskId: args.taskId,
                areaId: args.areaId
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to move task to capture area');
        }
    });
}
const taskProvenanceClient = createClient(CaptureTaskProvenanceService, connectTransport);
const TASK_PROVENANCE_KEY = 'capture-task-provenance';
/** SAD-486: area / origin / assignment stamp for every task of the pursuit. */ export function useCaptureTaskProvenance(captureProjectId) {
    return useQuery({
        queryKey: [
            TASK_PROVENANCE_KEY,
            captureProjectId
        ],
        enabled: !!captureProjectId,
        queryFn: async ()=>(await taskProvenanceClient.listCaptureTaskProvenance({
                captureProjectId
            })).provenance
    });
}
export function useSetCaptureTaskProvenance(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>taskProvenanceClient.setCaptureTaskProvenance(args, correlationHeader(workspaceCorrelationId)),
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    TASK_PROVENANCE_KEY,
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CAPTURE_PLAN_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update task area');
        }
    });
}
